/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import React, { Suspense, lazy } from "react";
import { getStickyToolVerbsDetails, getStickyToolVerbs } from "selectors/stickyToolsSelectors";
import { runInAction } from "mobx";
import { discovery } from "dc-core";
import { modernViewerDropinContext, VERB_COMMENT_STICKY_NOTE } from "constants/enums";
import { isBoolean } from "utils/lodashUtils";
import * as stores from "stores";
import { getFileExtension } from "selectors/documentSelectors";
import { getShouldUseMobileModernViewer } from "selectors/appSelectors";
import { copyPreviewSelection } from "utils/DropinUtils";
import StickyToolCss from "../components/stickyTools/StickyTools.css";
import { featureLifecycleHookController } from "../publicApis/controller/featureLifecycleHookController";
import { featureLifecyleHooks } from "../constants/enums";

const callDropinMethod = (dropinId, methodName, opts) => {
  const verbDetail = getStickyToolVerbs(stores)[dropinId];
  if (typeof verbDetail === "object") {
    return verbDetail.hasOwnProperty(methodName)
      ? Promise.resolve(verbDetail[methodName](opts))
      : Promise.reject(`${methodName} dosen't exist`);
  }

  /* Check if the verb has a load method defined. If yes, use it to load the verb before
   * accessing the method.
   * In case it fails, try to access bootstrap method.
   */
  return discovery
    .loadDropinClass(dropinId)
    .then(res => res[methodName](opts))
    .catch(() => discovery.callBootstrapMethod(dropinId, methodName, opts));
};

const getVerbBootstrap = verbId => {
  const verbDetail = getStickyToolVerbs(stores)[verbId];
  if (typeof verbDetail === "object") {
    return verbDetail;
  }
  return discovery.dropins[verbId] ? discovery.dropins[verbId].bootstrap : null;
};

export const callInitializeVerb = async (verbId, opts = {}, previewConfig) => {
  const bootstrap = getVerbBootstrap(verbId);
  if (bootstrap) {
    if (bootstrap.initializeGroup) {
      await callInitializeGroup(verbId, opts);
    } else if (bootstrap.initializeAction) {
      await callInitializeAction(verbId, { ...opts, previewConfig });
    } else if (bootstrap.load) {
      /* In case verb has a load method implemented, try to call both both initializeAction
       * and initializeGroup as we aren't sure which method would have been implemented
       * by the verb.
       */
      const res = callInitializeAction(verbId, { ...opts, previewConfig })
        .catch(() => callInitializeGroup(verbId, opts))
        .catch(() => {});
      await res;
    }
  }
};

export const callInitializeAction = async (dropinId, opts = {}) => {
  const selectionName = "viewerMegaverb";
  await copyPreviewSelection(selectionName);
  return new Promise((resolve, reject) => {
    callDropinMethod(dropinId, "initializeAction", {
      ...opts,
      context: modernViewerDropinContext.STICKY_TOOLS,
      isMobile: getShouldUseMobileModernViewer(stores),
      selectionName
    }).then(
      resp => {
        resolve(resp);
      },
      err => {
        reject(err);
      }
    );
  });
};

export const callInitializeGroup = (dropinId, opts = {}) =>
  new Promise((resolve, reject) => {
    callDropinMethod(dropinId, "initializeGroup", {
      ...opts,
      context: modernViewerDropinContext.STICKY_TOOLS,
      isMobile: getShouldUseMobileModernViewer(stores)
    }).then(
      resp => {
        resolve(resp);
      },
      err => {
        reject(err);
      }
    );
  });

const lazyLoadIcon = iconRender => {
  const IconAsync = lazy(iconRender);

  return (
    <div className={StickyToolCss.stickyIcon}>
      <Suspense fallback={<svg />}>
        <IconAsync />
      </Suspense>
    </div>
  );
};

export const fetchVerbIcon = (verbId, opts = {}) =>
  new Promise((resolve, reject) => {
    let method = "getIconAsync";
    callDropinMethod(verbId, method, {
      ...opts,
      context: modernViewerDropinContext.STICKY_TOOLS,
      isMobile: getShouldUseMobileModernViewer(stores)
    }).then(
      resp => {
        resolve(lazyLoadIcon(resp));
      },
      err => {
        // In case getIconAsync dosen't exist, try to call getIcon
        method = "getIcon";
        callDropinMethod(verbId, method, {
          ...opts,
          context: modernViewerDropinContext.STICKY_TOOLS,
          isMobile: getShouldUseMobileModernViewer(stores)
        }).then(
          resp => {
            resolve(resp);
          },
          err => {
            reject(err);
          }
        );
      }
    );
  });

export const fetchVerbLabel = (verbId, opts = {}) =>
  new Promise((resolve, reject) => {
    callDropinMethod(verbId, "getLabel", {
      ...opts,
      context: modernViewerDropinContext.STICKY_TOOLS,
      isMobile: getShouldUseMobileModernViewer(stores)
    }).then(
      resp => {
        resolve(resp);
      },
      err => {
        reject(err);
      }
    );
  });

const fetchActionTags = (verbId, opts = {}) =>
  new Promise((resolve, reject) => {
    callDropinMethod(verbId, "getActionTags", {
      ...opts,
      context: modernViewerDropinContext.STICKY_TOOLS,
      isMobile: getShouldUseMobileModernViewer(stores)
    }).then(
      resp => {
        resolve(resp);
      },
      err => {
        reject(err);
      }
    );
  });

export const fetchRelatedTools = (verbId, opts = {}) =>
  new Promise((resolve, reject) => {
    callDropinMethod(verbId, "getRelatedTools", {
      ...opts,
      context: modernViewerDropinContext.STICKY_TOOLS,
      isMobile: getShouldUseMobileModernViewer(stores)
    }).then(
      resp => {
        resolve(resp);
      },
      err => {
        resolve([]);
      }
    );
  });

export const callExecuteAction = async (verbId, opts = {}) => {
  const selectionName = "viewerMegaverb";
  await copyPreviewSelection(selectionName);
  return new Promise((resolve, reject) => {
    callDropinMethod(verbId, "executeAction", {
      ...opts,
      context: modernViewerDropinContext.STICKY_TOOLS,
      isMobile: getShouldUseMobileModernViewer(stores)
    }).then(
      resp => {
        resolve(resp);
      },
      err => {
        reject(err);
      }
    );
  });
};

export const callExecuteGroup = async (verbId, opts = {}) => {
  // TODO: Update selectionName here.
  const selectionName = "viewerMegaverb";
  await copyPreviewSelection(selectionName);
  return new Promise((resolve, reject) => {
    callDropinMethod(verbId, "executeGroup", {
      ...opts,
      context: modernViewerDropinContext.STICKY_TOOLS,
      selectionName,
      isMobile: getShouldUseMobileModernViewer(stores)
    }).then(
      resp => {
        resolve(resp);
      },
      err => {
        reject(err);
      }
    );
  });
};

// @TODO : Remove Catch block
export const fetchFileSelectionParams = (verbId, opts = {}) =>
  new Promise((resolve, reject) => {
    callDropinMethod(verbId, "getFileSelectionParams", {
      ...opts,
      context: modernViewerDropinContext.STICKY_TOOLS,
      isMobile: getShouldUseMobileModernViewer(stores)
    }).then(
      resp => {
        resolve(resp);
      },
      err => {
        reject(err);
      }
    );
  }).catch(() => {
    // TODO : Remove this after getFileSelectionParams is implemented by group-convert
    if (verbId === "group-convert")
      return Promise.resolve({
        acceptExtensions: ["pdf", "docx", "doc", "jpg", "jpeg", "png", "ppt", "pptx", "xls", "xlsx"]
      });
  });

export const doesToolSupportFileExtension = verbId => {
  const fileExtension = getFileExtension(stores).toLowerCase();
  return fetchFileSelectionParams(verbId).then(
    fileSelectionParams => fileSelectionParams && fileSelectionParams.acceptExtensions.includes(fileExtension)
  );
};

const isVerbDetailPresent = verbId => {
  const stickyToolVerbsDetails = getStickyToolVerbsDetails(stores) || {};
  return stickyToolVerbsDetails[verbId] && stickyToolVerbsDetails[verbId].label;
};

export const getVerbDetails = async verbId => {
  if (isVerbDetailPresent(verbId)) return (getStickyToolVerbsDetails(stores) || {})[verbId];

  const icon = fetchVerbIcon(verbId);
  const label = fetchVerbLabel(verbId);
  const relatedTools = fetchRelatedTools(verbId);
  const isFileTypeSupported = doesToolSupportFileExtension(verbId);

  const verbConfig = await Promise.all([icon, label, relatedTools]).catch(() => []);
  const isFileSupported = await isFileTypeSupported;

  if ((verbConfig || []).length > 0)
    return {
      verbId,
      icon: verbConfig[0],
      label: verbConfig[1],
      relatedTools: verbConfig[2],
      disabled: !isFileSupported
    };
};

const isVerbInitialized = verbId => {
  const stickyToolVerbsDetails = getStickyToolVerbsDetails(stores) || {};
  return stickyToolVerbsDetails[verbId] && stickyToolVerbsDetails[verbId].initialized;
};

export const showVerbCallback = verb => show => {
  if (isBoolean(show)) {
    runInAction(() => {
      stores.stickyToolsStore.updatePropertyForVerb(verb, "show", show);
    });
  }
};

export const enableVerbCallback = verb => enable => {
  if (isBoolean(enable)) {
    runInAction(() => {
      stores.stickyToolsStore.updatePropertyForVerb(verb, "disabled", !enable);
    });
  }
};

// TODO : Remove this function after verb-comment-stickynote starts using enableCallback
export const isDisabled = verb => disable => {
  if (isBoolean(disable)) {
    runInAction(() => {
      stores.stickyToolsStore.updatePropertyForVerb(verb, "disabled", disable);
    });
  }
};

export const populateStickyToolVerbs = async previewConfig => {
  const { stickyToolsStore } = stores;
  const filteredVerbs = getStickyToolVerbs(stores);

  // Populate data for each verb in store
  let verbsData = await Promise.all(
    Object.keys(filteredVerbs).map(verbId => getVerbDetails(verbId, previewConfig))
  ).then(verbs => verbs.filter(verb => verb));

  let verbDetails = verbsData.reduce((v1, v2) => ({ ...v1, [v2.verbId]: v2 }), {});
  verbsData.forEach(verbData => {
    const { verbId } = verbData;
    // Initialise  verbs
    if (!isVerbInitialized(verbId)) {
      const opts = {
        showCallback: showVerbCallback(verbId),
        enableCallback: enableVerbCallback(verbId),
        ...(verbId === VERB_COMMENT_STICKY_NOTE ? { isDisabled: isDisabled(verbId) } : {})
      };
      callInitializeVerb(verbId, opts, previewConfig);
      verbDetails[verbId].initialized = true;
    }
  });
  stickyToolsStore.setStickyToolVerbsDetail(verbDetails);
};

export const callExecuteVerb = async verbId => {
  const bootstrap = getVerbBootstrap(verbId);

  if (bootstrap) {
    if (bootstrap.getActionTags) {
      const actionTags = await fetchActionTags(verbId);
      if ((actionTags || []).includes(modernViewerDropinContext.REDIRECT_TO_APP)) {
        stores.appStore.setShowGetAppDialog(true);
        return;
      }
    }

    if (bootstrap.executeGroup) {
      callExecuteGroup(verbId);
    } else if (bootstrap.executeAction) {
      callExecuteAction(verbId);
    } else if (bootstrap.load) {
      /* In case verb has a load method implemented, try to call both both executeAction
       * and executeGroup as we aren't sure which method would have been implemented
       * by the verb.
       */
      callExecuteAction(verbId)
        .catch(() => callExecuteGroup(verbId))
        .catch(() => {});
    }
  }
};

export const stickyToolClickHelper = async (event, stickyToolData) => {
  event.preventDefault();
  event.stopPropagation();
  // [Bugfix] - ADCS-33832: Blur element after the Sticky tool element is clicked.
  blurElement(document.activeElement);
  const { verbId } = stickyToolData;
  try {
    await featureLifecycleHookController.trigger(featureLifecyleHooks.BEFORE_STICKY_TOOL_ACTIVATED, verbId);
    const stickyToolsVerbDetails = getStickyToolVerbsDetails(stores) || {};
    if (stickyToolsVerbDetails[verbId]) {
      await callExecuteVerb(verbId);
    }
  } catch (e) {
    console.error(e);
  }
};

const blurElement = element => {
  element && element.blur();
};
